import React, { useEffect, useMemo, useState, Suspense, lazy, createElement } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDownIcon, SlidersHorizontalIcon } from 'lucide-react';
import { Header } from './components/Header';
import { HeroSection } from './components/HeroSection';
import { FilterBar } from './components/FilterBar';
import { SchoolListings } from './components/SchoolListings';
import { ContentSection } from './components/ContentSection';
import { SeoContentSection } from './components/SeoContentSection';
import { Footer } from './components/Footer';
// Code-split route bundles — only the home page ships with the initial bundle.
const ArticlesPage = lazy(() => import('./pages/ArticlesPage').then(m => ({
  default: m.ArticlesPage
})));
const CollegesPage = lazy(() => import('./pages/CollegesPage').then(m => ({
  default: m.CollegesPage
})));
const ArticleDetailPage = lazy(() => import('./pages/ArticleDetailPage').then(m => ({
  default: m.ArticleDetailPage
})));
const ProgramLandingPage = lazy(() => import('./pages/ProgramLandingPage').then(m => ({
  default: m.ProgramLandingPage
})));
const TermsPage = lazy(() => import('./pages/TermsPage').then(m => ({
  default: m.TermsPage
})));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage').then(m => ({
  default: m.CookiePolicyPage
})));
const CANoticePage = lazy(() => import('./pages/CANoticePage').then(m => ({
  default: m.CANoticePage
})));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage').then(m => ({
  default: m.PrivacyPolicyPage
})));
const RequestInformationPage = lazy(() => import('./pages/RequestInformationPage').then(m => ({
  default: m.RequestInformationPage
})));
const RequestInformationNewStartAssistancePage = lazy(() => import('./pages/RequestInformationNewStartAssistancePage').then(m => ({
  default: m.RequestInformationNewStartAssistancePage
})));
const ExploreBridgePage = lazy(() => import('./pages/ExploreBridgePage').then(m => ({
  default: m.ExploreBridgePage
})));
const RequestInformationV1Page = lazy(() => import('./pages/RequestInformationV1Page').then(m => ({
  default: m.RequestInformationV1Page
})));
const AreaOfStudyLandingPage = lazy(() => import('./pages/AreaOfStudyLandingPage').then(m => ({
  default: m.AreaOfStudyLandingPage
})));
const RequestInformationV3Page = lazy(() => import('./pages/RequestInformationV3Page').then(m => ({
  default: m.RequestInformationV3Page
})));
const FindDegreesBridgePage = lazy(() => import('./pages/FindDegreesBridgePage').then(m => ({
  default: m.FindDegreesBridgePage
})));
const AdvertiserTemplatePage = lazy(() => import('./pages/AdvertiserTemplatePage').then(m => ({
  default: m.AdvertiserTemplatePage
})));
const PartnerCollegesPage = lazy(() => import('./pages/PartnerCollegesPage').then(m => ({
  default: m.PartnerCollegesPage
})));
const CrestmontUniversityPage = lazy(() => import('./pages/CrestmontUniversityPage').then(m => ({
  default: m.CrestmontUniversityPage
})));
const CrestmontProgramPage = lazy(() => import('./pages/CrestmontProgramPage').then(m => ({
  default: m.CrestmontProgramPage
})));
import { useSchoolSearch } from './hooks/useSchoolSearch';
import { useGeoLocation } from './hooks/useGeoLocation';
import { useMetaTags } from './hooks/useMetaTags';
import { useMetaPixel } from './hooks/useMetaPixel';
import { useUrlParams } from './hooks/useUrlParams';
import { areaOfStudyData } from './data/areaOfStudyData';
import { useFacebookDomainVerification } from './hooks/useFacebookDomainVerification';
import { getDefaultProgram, getAreaFromProgram, isValidProgram } from './data/filterOptions';
function FilterAccordion({
  degreeLevel,
  selectedAreaId,
  selectedProgram,
  loading,
  schoolCount,
  onDegreeLevelChange,
  onAreaStudyChange,
  onProgramChange
}: {
  degreeLevel: number;
  selectedAreaId: number;
  selectedProgram: number;
  loading: boolean;
  schoolCount: number;
  onDegreeLevelChange: (v: number) => void;
  onAreaStudyChange: (v: number) => void;
  onProgramChange: (v: number) => void;
}) {
  const [isOpen, setIsOpen] = useState(true);
  return <div className="w-full bg-white border-b border-gray-200 shadow-sm relative z-30" data-id="element-0" data-mp="c9kh4fku197u">
      {/* Accordion toggle — works on both mobile and desktop */}
      <div className="max-w-7xl mx-auto px-4" data-id="element-1" data-mp="14x6idh6l81rj">
        <button type="button" onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between py-3 md:py-4 hover:bg-gray-50/50 transition-colors rounded-lg px-2" data-id="element-2" data-mp="14xg9j83dceyt">
          <div className="flex items-center gap-2.5" data-id="element-3" data-mp="vyl25sol8ytx">
            <SlidersHorizontalIcon className="w-4 h-4 text-[#3b82f6]" data-id="element-4" data-mp="3kp41ep3izhar" />
            <span className="text-sm font-bold text-gray-900" data-id="element-5" data-mp="3gmpnmjepr7qc">
              Find Your Program
            </span>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full" data-id="element-6" data-mp="1w58292a78wun">
              {loading ? '...' : `${schoolCount} result${schoolCount !== 1 ? 's' : ''}`}
            </span>
          </div>
          <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-7" data-mp="2530h65c0e2hi" />
        </button>
      </div>

      {/* Collapsible content */}
      <div className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[500px] opacity-100 overflow-visible' : 'max-h-0 opacity-0 overflow-hidden'}`} data-id="element-8" data-mp="2ex966j1tzdax">
        <div className="px-4 pb-4 pt-1 max-w-7xl mx-auto" data-id="element-9" data-mp="2nwe4ppde76n3">
          <p className="text-xs text-gray-500 mb-3" data-id="element-10" data-mp="2suxslvbjqsi3">
            Select your degree of interest, area of interest, and program to see
            matching schools.
          </p>

          <FilterBar degreeLevel={degreeLevel} areaStudy={selectedAreaId} program={selectedProgram} onDegreeLevelChange={onDegreeLevelChange} onAreaStudyChange={onAreaStudyChange} onProgramChange={onProgramChange} data-id="element-11" data-mp="ygepprn3emzj" />

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 pt-3 border-t border-gray-100" data-id="element-12" data-mp="2as0iivhid7cx">
            <span className="text-xs text-gray-500 flex items-center gap-1.5" data-id="element-13" data-mp="25snwgcygo4be">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" data-id="element-14" data-mp="2kqga0zkg6imy"></span>
              All programs are accredited
            </span>
            <span className="text-xs text-gray-400" data-id="element-15" data-mp="2fqadp638g7yj">•</span>
            <span className="text-xs text-gray-500" data-id="element-16" data-mp="2tiqk8gvceo47">100% online options</span>
            <span className="text-xs text-gray-400 hidden sm:inline" data-id="element-17" data-mp="2tkum6t9old1u">•</span>
            <span className="text-xs text-gray-500 hidden sm:inline" data-id="element-18" data-mp="2pig6gi1b6tes">
              Financial aid eligible
            </span>
          </div>
        </div>
      </div>
    </div>;
}
export function App() {
  // Meta Business Manager domain verification tag.
  useFacebookDomainVerification();
  // Set favicon
  useEffect(() => {
    const favicon = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link');
    favicon.type = 'image/svg+xml';
    favicon.rel = 'icon';
    favicon.href = "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><text y='0.9em' font-size='90'>🎓</text></svg>";
    if (!document.querySelector("link[rel*='icon']")) {
      document.head.appendChild(favicon);
    }
  }, []);
  const location = useLocation();
  const navigate = useNavigate();
  const urlParams = useUrlParams();
  // Derive initial state from URL params or use defaults
  const initialProgram = urlParams.mProgram && isValidProgram(urlParams.mProgram) ? urlParams.mProgram : 900;
  const initialArea = urlParams.mProgram ? getAreaFromProgram(urlParams.mProgram) : 9;
  // Default the degree level filter to Bachelor's (value 3) when no
  // degreeInterest is supplied via URL params.
  const initialDegree = urlParams.degreeInterest ?? 3;
  // Derive currentPage from URL path
  const getPageFromPath = (pathname: string) => {
    const path = pathname.replace(/^\/+|\/+$/g, '');
    // Handle dynamic routes
    const pathMap: Record<string, string> = {
      'request-information': 'request-information',
      articles: 'articles',
      colleges: 'colleges',
      terms: 'terms',
      'privacy-policy': 'privacy-policy',
      'cookie-policy': 'cookie-policy',
      'ca-notice': 'ca-notice',
      'program-landing': 'program-landing',
      'partner-colleges': 'partner-colleges'
    };
    return pathMap[path] || 'compare';
  };
  const [currentPage, setCurrentPage] = useState<string>(getPageFromPath(location.pathname));
  const [currentArticle, setCurrentArticle] = useState<string | null>(null);
  const [degreeLevel, setDegreeLevel] = useState<number>(initialDegree);
  const [selectedAreaId, setSelectedAreaId] = useState<number>(initialArea);
  const [selectedProgram, setSelectedProgram] = useState<number>(initialProgram);
  // Sync currentPage when location changes (e.g. Magic Patterns route dropdown)
  useEffect(() => {
    setCurrentPage(getPageFromPath(location.pathname));
  }, [location.pathname]);
  // Sync filter state when URL params change (e.g. arriving from /find-degrees/v6)
  useEffect(() => {
    if (urlParams.mProgram && isValidProgram(urlParams.mProgram)) {
      setSelectedProgram(urlParams.mProgram);
      setSelectedAreaId(getAreaFromProgram(urlParams.mProgram));
    }
    if (urlParams.degreeInterest != null) {
      setDegreeLevel(urlParams.degreeInterest);
    }
  }, [urlParams.mProgram, urlParams.degreeInterest]);
  // Build tracking params from URL (falls back to defaults inside the hook)
  const tracking = useMemo(() => ({
    sub1: urlParams.sub1 ?? undefined,
    sub2: urlParams.sub2 ?? undefined,
    sub3: urlParams.sub3 ?? undefined
  }), [urlParams]);
  const {
    geo
  } = useGeoLocation();
  useMetaPixel();
  const {
    schools,
    loading,
    error
  } = useSchoolSearch({
    degreeInterest: degreeLevel,
    mProgram: selectedProgram,
    zipCode: geo.zipCode,
    tracking
  });
  const currentAreaData = areaOfStudyData[selectedAreaId];
  const metaConfig = useMemo(() => {
    const configs: Record<string, {
      title: string;
      description: string;
      keywords: string;
    }> = {
      compare: {
        title: `Compare Online ${currentAreaData.name} Programs | DegreeCompare`,
        description: `Compare top accredited online ${currentAreaData.name.toLowerCase()} programs. Find the best online colleges, read reviews, and explore degree options for ${new Date().getFullYear()}.`,
        keywords: `online ${currentAreaData.name.toLowerCase()} degree, accredited online programs, compare online colleges, ${currentAreaData.name.toLowerCase()} programs, online education`
      },
      colleges: {
        title: 'Explore Accredited Online Colleges | DegreeCompare',
        description: 'Browse top-rated accredited online colleges and universities. Compare programs, read reviews, and find the right school for your online degree.',
        keywords: 'online colleges, accredited universities, online degree programs, best online schools, college directory'
      },
      articles: {
        title: 'Articles & Guides for Online Students | DegreeCompare',
        description: 'Expert advice on choosing the right online program, financial aid, career planning, and more. Resources for prospective and current online students.',
        keywords: 'online education articles, college guides, financial aid tips, career planning, online degree advice'
      }
    };
    return configs[currentPage] || configs.compare;
  }, [currentPage, currentAreaData]);
  useMetaTags({
    title: metaConfig.title,
    description: metaConfig.description,
    keywords: metaConfig.keywords,
    ogTitle: metaConfig.title,
    ogDescription: metaConfig.description
  });
  const handleAreaChange = (areaId: number) => {
    setSelectedAreaId(areaId);
    setSelectedProgram(getDefaultProgram(areaId));
  };
  const handleProgramChange = (programId: number) => {
    // Derive the area from the program
    const newAreaId = getAreaFromProgram(programId);
    // If the area changes, update both area and program
    if (newAreaId !== selectedAreaId) {
      setSelectedAreaId(newAreaId);
    }
    setSelectedProgram(programId);
  };
  const handleNavigate = (page: string, articleSlug?: string) => {
    setCurrentPage(page);
    if (articleSlug) {
      setCurrentArticle(articleSlug);
    } else {
      setCurrentArticle(null);
    }
    // Update URL to match the current page
    let urlPath = '/';
    if (page === 'compare') {
      urlPath = '/';
    } else if (page === 'articles' && articleSlug) {
      // Article detail pages live at /articles/<slug>
      urlPath = `/articles/${articleSlug}`;
    } else if (page.startsWith('university/')) {
      urlPath = `/${page}`;
    } else {
      urlPath = `/${page}`;
    }
    navigate(urlPath);
    window.scrollTo(0, 0);
  };
  const handleNavigateToProgram = (areaId: number, programId: number) => {
    setSelectedAreaId(areaId);
    setSelectedProgram(programId);
    // Stay on compare page to show filtered results
    window.scrollTo(0, 0);
  };
  const handleNavigateToDegree = (degree: number) => {
    setDegreeLevel(degree);
    setSelectedAreaId(9);
    setSelectedProgram(900);
    setCurrentPage('compare');
    navigate('/');
    window.scrollTo(0, 0);
  };
  // Shared layout wrapper
  const isAdvertiserPage = location.pathname.startsWith('/universities/');
  // Explore bridge pages render their own nav + footer.
  const isBridgePage = location.pathname.startsWith('/find-degrees/') || location.pathname.startsWith('/explore/');
  // The v3 request-information template renders its own header + footer.
  const isSelfContainedTemplate = location.pathname.startsWith('/request-information/v3');
  const PageLayout = ({
    children
  }: {
    children: React.ReactNode;
  }) => {
    if (isAdvertiserPage || isBridgePage || isSelfContainedTemplate) {
      return <div className="min-h-screen bg-gray-50 font-sans text-gray-900 flex flex-col" data-id="element-19" data-mp="qnrkq1k1flwv">
          <main className="flex-grow" data-id="element-20" data-mp="vmkzryez3kz5">{children}</main>
        </div>;
    }
    return <div className="min-h-screen bg-gray-100 font-sans text-gray-900 flex flex-col" data-id="element-21" data-mp="vk89mxz2a4b0">
        <Header currentPage={currentPage} onNavigate={handleNavigate} onNavigateToProgram={handleNavigateToProgram} onNavigateToDegree={handleNavigateToDegree} data-id="element-22" data-mp="10jl7edrh0vsd" />
        <main className="flex-grow" data-id="element-23" data-mp="1aduzrvgexu33">{children}</main>
        <Footer onNavigate={handleNavigate} data-id="element-24" data-mp="1ec14kxxoxzld" />
      </div>;
  };
  const ComparePage = () => <>
      <HeroSection areaData={currentAreaData} selectedProgram={selectedProgram} degreeLevel={degreeLevel} selectedAreaId={selectedAreaId} t1Override={urlParams.t1} data-id="element-25" data-mp="1jdi00dxeotuk" />
      <FilterAccordion degreeLevel={degreeLevel} selectedAreaId={selectedAreaId} selectedProgram={selectedProgram} loading={loading} schoolCount={schools.length} onDegreeLevelChange={setDegreeLevel} onAreaStudyChange={handleAreaChange} onProgramChange={handleProgramChange} data-id="element-26" data-mp="1ygru51xzy5zl" />
      <SchoolListings schools={schools} loading={loading} error={error} selectedAreaId={selectedAreaId} selectedProgram={selectedProgram} onProgramChange={handleProgramChange} data-id="element-27" data-mp="2h5ezllk34xdp" />
      <SeoContentSection areaStudyId={selectedAreaId} topSchool={schools[0]} data-id="element-28" data-mp="27etstau9cmjp" />
      <ContentSection areaData={currentAreaData} selectedAreaId={selectedAreaId} onNavigate={handleNavigate} data-id="element-29" data-mp="23etdzp38y9c4" />
    </>;
  return <PageLayout data-id="element-30" data-mp="2zyae18dpjsty">
      <Suspense fallback={<div className="min-h-[60vh] flex items-center justify-center" data-id="element-32" data-mp="2v4el4kcaww5n">
            <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin" data-id="element-33" data-mp="t7dvv9uvhhcp" />
          </div>} data-id="element-31" data-mp="2v5h9olm5imie">
        <Routes data-id="element-34" data-mp="y6h1owiofrlq">
          <Route path="/" element={<ComparePage data-id="element-36" data-mp="ji427pkdrqmh" />} data-id="element-35" data-mp="y937aqm0bx1b" />
          <Route path="/request-information" element={<RequestInformationPage data-id="element-38" data-mp="oh779a0d1wug" />} data-id="element-37" data-mp="jivy71hw3qda" />
          <Route path="/request-information/v1" element={<RequestInformationV1Page data-id="element-40" data-mp="agwgs82yqk9t" />} data-id="element-39" data-mp="ofw3ha6x0p3c" />
          <Route path="/request-information/v3" element={<RequestInformationV3Page data-id="element-42" data-mp="fhaf2yerifun" />} data-id="element-41" data-mp="ajiny6lxyxri" />
          <Route path="/request-information/new-start-assistance" element={<RequestInformationNewStartAssistancePage data-id="element-44" data-mp="lmdlh0tw6ug" />} data-id="element-43" data-mp="j9omjwnkw7w" />
          {/* Explicit route so the first area landing shows up in the
              preview's route list; the dynamic route below serves every
              other <area-slug>/<version> combination. */}
          <Route path="/areas-of-study/psychology/v1" element={<AreaOfStudyLandingPage data-id="element-46" data-mp="3kfg79sn9zd7x" />} data-id="element-45" data-mp="1zvu9f56sqv0c" />
          <Route path="/areas-of-study/psychology/v2" element={<AreaOfStudyLandingPage data-id="element-48" data-mp="3fh4wnfp5oyon" />} data-id="element-47" data-mp="3kc22kdr52rxb" />
          <Route path="/areas-of-study/health-care/v1" element={<AreaOfStudyLandingPage data-id="element-50" data-mp="3u60z8x8g1jhv" />} data-id="element-49" data-mp="3u4py756dupug" />
          <Route path="/areas-of-study/:areaSlug/:version" element={<AreaOfStudyLandingPage data-id="element-52" data-mp="6yqnn2gf2jmi" />} data-id="element-51" data-mp="3pcps0uzpz0pf" />
          {/* Explicit route so this template shows up in the preview's
              route list; the dynamic route below serves every other
              {brand}-{version} slug. */}
          <Route path="/explore/UAGC-v1" element={<ExploreBridgePage data-id="element-54" data-mp="bylbex4wat3c" />} data-id="element-53" data-mp="gx650dpn6eit" />
          <Route path="/explore/UAGC-v2" element={<ExploreBridgePage data-id="element-56" data-mp="bwictopofrn0" />} data-id="element-55" data-mp="bzmot89dvro8" />
          <Route path="/explore/UMA-v1" element={<ExploreBridgePage data-id="element-58" data-mp="2ipuaktprde1r" />} data-id="element-57" data-mp="2iot0oskhaxlm" />
          <Route path="/explore/UMA-v2" element={<ExploreBridgePage data-id="element-60" data-mp="2dqrg237emq81" />} data-id="element-59" data-mp="2iqvxbvg4zozb" />
          <Route path="/explore/UMA-v3" element={<ExploreBridgePage data-id="element-62" data-mp="1rwsgq4fqt2fd" />} data-id="element-61" data-mp="2drj2ooj0xdm6" />
          <Route path="/explore/search-v1" element={<ExploreBridgePage data-id="element-64" data-mp="1rups9ou8nhcr" />} data-id="element-63" data-mp="1rvr6g26jwjyp" />
          <Route path="/explore/:bridgeSlug" element={<ExploreBridgePage data-id="element-66" data-mp="1wtsqapbswnb8" />} data-id="element-65" data-mp="1wvdi476qds5e" />
          <Route path="/colleges" element={<CollegesPage data-id="element-68" data-mp="21n3ueip8apzg" />} data-id="element-67" data-mp="21mc1xgqxh8de" />
          <Route path="/partner-colleges" element={<PartnerCollegesPage data-id="element-70" data-mp="26knalo5hlcu1" />} data-id="element-69" data-mp="21oya9iln7rwo" />
          {/* Crestmont University runs its own brand-guide experience rather
              than the shared advertiser template. Listed before the generic
              :collegeSlug routes so it wins the match. */}
          <Route path="/universities/crestmont-university" element={<CrestmontUniversityPage data-id="element-72" data-mp="1iut03d8n40cy" />} data-id="element-71" data-mp="1dyuge2y9b6iv" />
          <Route path="/universities/crestmont-university/bba-programs" element={<CrestmontProgramPage slug="bba-programs" data-id="element-74" data-mp="2ubsovyvs3x28" />} data-id="element-73" data-mp="1nvqhhnltsqde" />
          <Route path="/universities/crestmont-university/aba-programs" element={<CrestmontProgramPage slug="aba-programs" data-id="element-76" data-mp="2zcgqfehmvwap" />} data-id="element-75" data-mp="2udlxvktv6tde" />
          <Route path="/universities/:collegeSlug" element={<AdvertiserTemplatePage data-id="element-78" data-mp="ine750ktgipn" />} data-id="element-77" data-mp="nlpff6vgql5r" />
          <Route path="/universities/:collegeSlug/:programSlug" element={<AdvertiserTemplatePage data-id="element-80" data-mp="dvmnla02vd90" />} data-id="element-79" data-mp="iq0mvnhv24iy" />
          <Route path="/articles" element={<ArticlesPage onNavigate={handleNavigate} data-id="element-82" data-mp="8xul5m1t48qk" />} data-id="element-81" data-mp="8ye67bmr7d8t" />
          <Route path="/articles/:slug" element={<ArticleDetailPage onNavigate={handleNavigate} onProgramChange={handleProgramChange} data-id="element-84" data-mp="3w2arwxnazwdm" />} data-id="element-83" data-mp="402ronlikek9" />
          <Route path="/program-landing" element={<ProgramLandingPage programId={selectedProgram} degreeLevel={degreeLevel} onProgramChange={handleProgramChange} data-id="element-86" data-mp="3m6h6eorlwsjp" />} data-id="element-85" data-mp="3r5kbuabi10s7" />
          <Route path="/terms" element={<TermsPage data-id="element-88" data-mp="2c72rqr08o35a" />} data-id="element-87" data-mp="1kb6prz5e4vds" />
          <Route path="/cookie-policy" element={<CookiePolicyPage data-id="element-90" data-mp="2c9f6ywem3zen" />} data-id="element-89" data-mp="2c6axbjidobgl" />
          <Route path="/ca-notice" element={<CANoticePage data-id="element-92" data-mp="279kggtyr1bu4" />} data-id="element-91" data-mp="278j6ytxdsxei" />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage data-id="element-94" data-mp="22h139y3z4ipu" />} data-id="element-93" data-mp="27accu7067dlg" />
          <Route path="/find-degrees/:templateId" element={<FindDegreesBridgePage data-id="element-96" data-mp="1xj7z1am8dys7" />} data-id="element-95" data-mp="22g83jh3fmth7" />
        </Routes>
      </Suspense>
    </PageLayout>;
}